import { useRef, useState } from 'react'
import { cn } from '../../lib/cva'
import { Modal } from '../Modal'
import { Spinner } from '../Spinner'
import { Text } from '../Text'
import type { ComponentProps, ImageCustomProps } from './types'
import { getLabelVariant } from './utils'

interface FormsImageProps extends ComponentProps {
	isUploading?: boolean
	enableLightbox?: boolean
	maxWidthPercent?: number
}

export const FormsImage = ({
	component,
	isUploading = false,
	enableLightbox = false,
	maxWidthPercent,
}: FormsImageProps) => {
	const { label, labelColor, labelSize, labelWeight, backgroundColor, imageUrl, caption, displayWidth } =
		component.custom as ImageCustomProps
	// Use displayWidth from custom props, fallback to maxWidthPercent prop, then 50% default
	const effectiveWidth = Number(displayWidth) || maxWidthPercent || 50
	const [isLightboxOpen, setIsLightboxOpen] = useState(false)
	const [scale, setScale] = useState(1)

	// Pan state
	const [position, setPosition] = useState({ x: 0, y: 0 })
	const [isDragging, setIsDragging] = useState(false)
	const dragStart = useRef({ x: 0, y: 0 })
	const positionStart = useRef({ x: 0, y: 0 })

	const handleImageClick = () => {
		if (enableLightbox && imageUrl) {
			setIsLightboxOpen(true)
			setScale(1)
			setPosition({ x: 0, y: 0 })
		}
	}

	const handleZoomIn = () => {
		setScale((prev) => Math.min(prev + 0.5, 3))
	}

	const handleZoomOut = () => {
		const newScale = Math.max(scale - 0.5, 0.5)
		setScale(newScale)
		// Reset position when zooming out to 1x or less
		if (newScale <= 1) {
			setPosition({ x: 0, y: 0 })
		}
	}

	const handleReset = () => {
		setScale(1)
		setPosition({ x: 0, y: 0 })
	}

	// Mouse drag handlers for panning
	const handleMouseDown = (e: React.MouseEvent) => {
		if (scale > 1) {
			setIsDragging(true)
			dragStart.current = { x: e.clientX, y: e.clientY }
			positionStart.current = { x: position.x, y: position.y }
			e.preventDefault()
		}
	}

	const handleMouseMove = (e: React.MouseEvent) => {
		if (isDragging && scale > 1) {
			const dx = e.clientX - dragStart.current.x
			const dy = e.clientY - dragStart.current.y
			setPosition({
				x: positionStart.current.x + dx,
				y: positionStart.current.y + dy,
			})
		}
	}

	const handleMouseUp = () => {
		setIsDragging(false)
	}

	const handleMouseLeave = () => {
		setIsDragging(false)
	}

	const showLabel = label && labelWeight !== 'hidden'

	return (
		<div
			className={cn('flex flex-col gap-2 w-full', backgroundColor && 'p-4 rounded-md')}
			style={{ backgroundColor: backgroundColor || undefined }}>
			{showLabel && (
				<Text
					variant={getLabelVariant(labelSize)}
					weight={labelWeight === 'semibold' ? 'semibold' : 'regular'}
					style={{ color: labelColor }}>
					{label}
				</Text>
			)}

			<div className='relative' style={{ maxWidth: `${effectiveWidth}%` }}>
				{isUploading ? (
					<div className='flex items-center justify-center h-32 bg-neutral-100 rounded-md border border-neutral-200'>
						<Spinner className='size-6' />
					</div>
				) : imageUrl ? (
					<img
						src={imageUrl}
						alt={caption || label || 'Form image'}
						role={enableLightbox ? 'button' : undefined}
						tabIndex={enableLightbox ? 0 : undefined}
						className={cn(
							'rounded-md max-w-full h-auto',
							enableLightbox && 'cursor-pointer hover:opacity-90 transition-opacity',
						)}
						onClick={handleImageClick}
						onKeyDown={
							enableLightbox
								? (e) => {
										if (e.key === 'Enter' || e.key === ' ') handleImageClick()
									}
								: undefined
						}
					/>
				) : (
					<div className='flex items-center justify-center h-32 bg-neutral-100 rounded-md border border-neutral-200'>
						<Text variant='small' className='text-neutral-400'>
							No image
						</Text>
					</div>
				)}
			</div>

			{caption && (
				<Text variant='xsmall' className='text-neutral-500 italic'>
					{caption}
				</Text>
			)}

			{/* Lightbox Modal */}
			{enableLightbox && (
				<Modal
					isOpen={isLightboxOpen}
					onClose={() => setIsLightboxOpen(false)}
					title={label || 'Image'}
					className='!w-[80vw] !max-w-[1000px]'
					bodyClassName='p-4'>
					<div className='flex flex-col gap-4'>
						<div className='flex justify-center items-center gap-2 mb-2'>
							<button
								type='button'
								onClick={handleZoomOut}
								className='px-3 py-1 text-sm rounded bg-neutral-100 hover:bg-neutral-200'
								disabled={scale <= 0.5}>
								−
							</button>
							<span className='px-3 py-1 text-sm text-neutral-600 min-w-16 text-center'>
								{Math.round(scale * 100)}%
							</span>
							<button
								type='button'
								onClick={handleZoomIn}
								className='px-3 py-1 text-sm rounded bg-neutral-100 hover:bg-neutral-200'
								disabled={scale >= 3}>
								+
							</button>
							<button
								type='button'
								onClick={handleReset}
								className='px-3 py-1 text-sm rounded bg-neutral-100 hover:bg-neutral-200 ml-2'>
								Reset
							</button>
						</div>
						<div
							role='application'
							aria-label='Zoomable image viewer'
							className='overflow-hidden max-h-[70vh] flex justify-center items-center bg-neutral-50 rounded-md'
							onMouseDown={handleMouseDown}
							onMouseMove={handleMouseMove}
							onMouseUp={handleMouseUp}
							onMouseLeave={handleMouseLeave}
							style={{ cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}>
							<img
								src={imageUrl}
								alt={caption || label || 'Form image'}
								draggable={false}
								style={{
									transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
									transformOrigin: 'center',
									transition: isDragging ? 'none' : 'transform 0.2s ease',
									userSelect: 'none',
								}}
							/>
						</div>
						{scale > 1 && (
							<Text variant='xsmall' className='text-neutral-400 text-center'>
								Click and drag to pan
							</Text>
						)}
						{caption && (
							<Text variant='small' className='text-neutral-500 text-center italic'>
								{caption}
							</Text>
						)}
					</div>
				</Modal>
			)}
		</div>
	)
}
